import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  type AdminBotProfileRequest,
  type AdminDiscordConnection,
  type AdminMemberTracking,
  type AdminUpdateDiscordBotRequest,
  type AdminUpdateDiscordEmbedsRequest,
  type AdminUpdateDiscordWebhooksRequest,
  adminUpdateDiscordEmbedsRequestSchema,
  adminUpdateDiscordWebhooksRequestSchema,
} from "@ternary/contracts"
import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  Field,
  FormWizard,
  IconButton,
  Input,
  Menu,
  NavTabs,
  PageHeader,
  Select,
  Tabs,
  Textarea,
  ToneIcon,
  useToast,
  type WizardStep,
} from "@ternary/ui"
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
} from "react"
import { flushSync } from "react-dom"
import { Link, useSearchParams } from "react-router-dom"
import {
  AdminApiError,
  checkMembersNow,
  getBotProfile,
  loadAdminDiscord,
  saveBotProfile,
  sendBotProfile,
  setAdminDiscordConnection,
  setMemberTracking,
  updateAdminDiscordBot,
  updateAdminDiscordEmbeds,
  updateAdminDiscordWebhooks,
} from "../api.js"
import { BotProfileCard } from "../components/bot-profile-card.js"
import {
  DiscordHealth,
  INSTALL_URL,
  InstallLink,
  serverStatus,
} from "../components/discord-health.js"
import { DocsLink } from "../components/docs-link.js"
import { FormLoading } from "../components/form-loading.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { MemberTrackingCard } from "../components/member-tracking-card.js"
import { NavIcon } from "../components/nav-icons.js"
import { ViewOnlyNotice } from "../components/view-only.js"
import { useMedia, useWide } from "../use-wide.js"
import { PollsPanel } from "./admin-polls-page.js"
import { SavedPostsComingSoon, SavedPostsPanel } from "./admin-saved-posts-page.js"

/** Below 640px the Server tab folds its webhooks, so the servers and Save fit a phone. */
const PHONE_QUERY = "(width < 40rem)"

const emojis = ["🚀", "🎓", "🔥", "✅", "💎", "⭐", "🎉", "❤️"]

/** The install's permissions, 805309459, in plain words. */
const BOT_PERMISSIONS = [
  "View channels",
  "Send messages",
  "Manage roles",
  "Create invites",
  "Manage channels",
  "Manage webhooks",
  "Kick members",
]

const INSTALL_ERRORS: Partial<Record<string, string>> = {
  "guild-claimed":
    "That server is already connected to another Ternary account. Choose a different server, or ask that account to remove it first.",
  "install-state":
    "That Discord install expired or is no longer valid, so nothing was added. Start it again.",
  "install-unconfirmed":
    "Discord didn't confirm the install, so no server was added. If you cancelled, start again and choose Authorize.",
}

const CHANNEL_HINT = "In Discord, right-click the channel and choose Copy Channel ID."
const MAX_REACTIONS = 25
/** Discord's limit on a message, which the bot settings and the embeds keep to. */
const MAX_MESSAGE = 2_000

/**
 * A message the bot sends: one line that grows to three (ruling R15), so a real message reads whole
 * and can be proofread, with Discord's limit counted beside its label.
 */
function MessageField({
  error,
  hint,
  label,
  onChange,
  required,
  value,
}: {
  error: string | undefined
  /** When it is sent, such as "Sent when a payment fails." */
  hint?: string
  label: string
  onChange(value: string): void
  required: boolean
  value: string
}) {
  return (
    <Field
      aside={<span className="admin-count">{`${value.length}/${MAX_MESSAGE}`}</span>}
      error={error}
      hint={error ? undefined : hint}
      label={label}
      required={required}
    >
      <Textarea
        className="admin-message-input"
        maxLength={MAX_MESSAGE}
        onChange={(event) => onChange(event.currentTarget.value)}
        rows={1}
        value={value}
      />
    </Field>
  )
}

type Errors = Partial<Record<string, string>>
type TabId = "server" | "automations" | "messages"
// The tabs: each form's, then Polls while the deployment offers them.
type SettingsTab = TabId | "polls" | "saved-posts"
type Message = { tab: TabId; text: string; error?: boolean }

/** The Connections title and the tabs between its three pages. */
export function ConnectionsHeader({ active }: { active: "discord" | "pixels" | "stripe" }) {
  return (
    <div className="admin-stack">
      <PageHeader actions={<DocsLink />} title="Connections" />
      <NavTabs label="Connections">
        <Link aria-current={active === "discord" ? "page" : undefined} to="/dashboard/discord">
          Discord
        </Link>
        <Link aria-current={active === "stripe" ? "page" : undefined} to="/dashboard/stripe">
          Payment Gateways
        </Link>
        <Link aria-current={active === "pixels" ? "page" : undefined} to="/dashboard/pixels">
          Tracking Pixels
        </Link>
      </NavTabs>
    </div>
  )
}

function roleCount(count: number) {
  return `${count} ${count === 1 ? "role" : "roles"}`
}

/**
 * A form's draft and its errors. Editing a field takes its error away until the next Save, and a
 * blocked Save focuses the first invalid field.
 */
function useCheckedForm<D>(initial: () => D, validate: (draft: D) => Errors) {
  const formRef = useRef<HTMLFormElement>(null)
  const [draft, setDraft] = useState(initial)
  const [errors, setErrors] = useState<Errors>({})

  function edit(update: Partial<D>, ...fields: string[]) {
    setDraft((current) => ({ ...current, ...update }))
    if (fields.some((field) => errors[field])) {
      setErrors((current) =>
        Object.fromEntries(Object.entries(current).filter(([key]) => !fields.includes(key))),
      )
    }
  }

  /** `reveal` shows the first error's panel, when the form spans tabs. */
  function submit(event: FormEvent, onValid: (value: D) => void, reveal?: (found: Errors) => void) {
    event.preventDefault()
    const found = validate(draft)
    const blocked = Object.values(found).some(Boolean)
    // Rendered now, so the first invalid field can take focus.
    flushSync(() => {
      setErrors(found)
      if (blocked) reveal?.(found)
    })
    if (blocked) {
      const invalid = formRef.current?.querySelectorAll<HTMLElement>('[aria-invalid="true"]') ?? []
      ;[...invalid].find((field) => !field.closest("[hidden]"))?.focus()
      return
    }
    onValid(draft)
  }

  return { draft, edit, errors, formRef, setDraft, submit }
}

/** A Save outside the keyed form, so its messages survive the reset that a save causes. */
function SaveBar({
  form,
  message,
  pending,
  writable,
}: {
  form: string
  message: Message | undefined
  pending: boolean
  writable: boolean
}) {
  // The page's view-only notice says why there is no Save.
  if (!writable) return null
  return (
    <div className="admin-save-bar">
      {message?.error ? (
        <p className="admin-save-message tny-error" role="alert">
          {message.text}
        </p>
      ) : null}
      <p
        className={
          message && !message.error ? "admin-save-message is-success" : "admin-save-message"
        }
        role="status"
      >
        {message && !message.error ? (
          <>
            <ToneIcon tone="success" />
            {message.text}
          </>
        ) : null}
      </p>
      <Button form={form} loading={pending} type="submit">
        Save
      </Button>
    </div>
  )
}

// The wizard: shown until a server is connected, and kept to its end once it starts.

function ConnectWizard({
  catalog,
  checking,
  installError,
  onCheck,
  onFinish,
  stepId,
}: {
  catalog: AdminDiscordConnection
  checking: boolean
  installError: string | null
  onCheck(): Promise<void>
  onFinish(): void
  stepId: string | undefined
}) {
  const { connection, roles, writable } = catalog
  const serverName = connection.serverName || "Your server"
  const steps: WizardStep<Record<string, never>>[] = [
    {
      id: "invite",
      title: "Add the bot",
      description:
        "Discord asks you to choose the server your members join and to approve the bot, then brings you back here.",
      // Adding the bot is the way on; this one only checks for a bot added before.
      nextLabel: "I've already added it, continue",
      nextVariant: "secondary",
      render: () => (
        <div className="admin-stack">
          {installError ? (
            <p className="tny-error" role="alert">
              {installError}
            </p>
          ) : null}
          {writable ? (
            <div className="admin-actions">
              <InstallLink variant="primary" writable={writable}>
                Add the bot
              </InstallLink>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      id: "confirm",
      title: "Confirm your server",
      description: "Check this is the server your members join.",
      validate: () =>
        connection.connected
          ? {}
          : { server: "We can't see the bot in a server yet. Add it, then check again." },
      render: ({ errors }) =>
        connection.connected ? (
          <div className="admin-stack">
            <div className="admin-server-card">
              <span className="admin-server-text">
                <span className="admin-server-name">{serverName}</span>
                <span className="admin-note">
                  <span>{roleCount(roles.length)}</span> · Server ID{" "}
                  <span className="admin-mono">{connection.serverId}</span>
                </span>
              </span>
              <Badge tone="success">Bot connected</Badge>
            </div>
            <p className="admin-heading">The bot can only do what members need:</p>
            <ul
              aria-label="What the bot can do"
              className="admin-permissions"
              // biome-ignore lint/a11y/noRedundantRoles: Safari drops the list role of a list styled with list-style: none.
              role="list"
            >
              {BOT_PERMISSIONS.map((permission) => (
                <li key={permission}>{permission}</li>
              ))}
            </ul>
            <p className="admin-note">
              It never asks for Administrator. Its #ternary-admin and #ternary-rewards channels are
              admin-only.
            </p>
          </div>
        ) : (
          <div className="admin-stack">
            {errors.server ? (
              <p className="tny-error" role="alert">
                {errors.server}
              </p>
            ) : (
              <p className="admin-note">
                We can't see the bot in a server yet. Add it, then check again.
              </p>
            )}
            <div className="admin-actions">
              <Button loading={checking} onClick={() => void onCheck()} variant="secondary">
                Check again
              </Button>
            </div>
          </div>
        ),
    },
    {
      id: "done",
      title: "Done",
      render: () => (
        <div className="admin-stack">
          <p className="admin-note">
            {serverName} is connected. Next, create a product that gives members access to it.
          </p>
          <div className="admin-actions">
            <Link
              className="tny-button tny-button-secondary admin-button-link"
              to="/dashboard/products/new"
            >
              Create a product
            </Link>
          </div>
        </div>
      ),
    },
  ]
  return (
    <FormWizard
      headingLevel={2}
      initialDraft={{}}
      onStepSubmit={async (id) => {
        if (id === "invite") await onCheck()
      }}
      onSubmit={onFinish}
      stepId={stepId}
      steps={steps}
      submitLabel="Finish"
      title="Connect Discord"
    />
  )
}

// The Server tab: the connected servers, the install and removal, and the webhooks.

/** A server's ID, with Copy: the whole ID, for a support request or a bot's settings. */
function ServerId({ id }: { id: string }) {
  const { toast } = useToast()

  async function copy() {
    try {
      await navigator.clipboard.writeText(id)
      toast({ message: "Server ID copied.", tone: "success" })
    } catch {
      toast({ message: `The server ID could not be copied: ${id}`, tone: "error" })
    }
  }

  return (
    <>
      <span className="admin-server-id-text">
        ID <span className="admin-mono">{id}</span>
      </span>
      <IconButton
        icon={<NavIcon name="copy" />}
        label="Copy server ID"
        onClick={() => void copy()}
        size="sm"
      />
    </>
  )
}

/**
 * The connected servers, in one card: each its name, one status badge, its ID with Copy, its bot's
 * health, and its own menu (Re-add the bot, and Remove bot on the primary, the server the bot
 * leaves). Add a server is the page's one action.
 */
function ServerList({
  catalog,
  checking,
  onCheck,
  onRemove,
}: {
  catalog: AdminDiscordConnection
  checking: boolean
  onCheck(): Promise<unknown>
  onRemove(): void
}) {
  const { connection, writable } = catalog
  // A primary saved before guilds existed is listed from the connection.
  const guilds =
    catalog.guilds.length > 0
      ? catalog.guilds
      : connection.serverId
        ? [{ id: connection.serverId, name: connection.serverName ?? "", primary: true }]
        : []
  return (
    <div className="admin-stack admin-stack-tight">
      <ul
        aria-label="Connected servers"
        className="admin-server-list"
        // biome-ignore lint/a11y/noRedundantRoles: Safari drops the list role of a list styled with list-style: none.
        role="list"
      >
        {guilds.map((guild) => {
          const name = guild.name || "Unnamed server"
          // The connection's status is the primary server's.
          const status = serverStatus(guild, !guild.primary || connection.connected)
          return (
            <li className="admin-server" key={guild.id}>
              <span className="admin-server-head">
                <span className="admin-server-name">{name}</span>
                {guild.primary ? <Badge>Primary</Badge> : null}
                <Badge tone={status.tone}>{status.label}</Badge>
              </span>
              <span className="admin-server-id">
                <ServerId id={guild.id} />
                {writable ? (
                  <Menu
                    icon={<NavIcon name="more" />}
                    // Its own ids, so the forms below keep theirs.
                    id={`discord-server-${guild.id}`}
                    items={[
                      {
                        href: guild.reinstallUrl ?? `${INSTALL_URL}?guildId=${guild.id}`,
                        label: "Re-add the bot",
                      },
                      ...(guild.primary && connection.connected && connection.manageAvailable
                        ? [{ danger: true, label: "Remove bot", onSelect: onRemove }]
                        : []),
                    ]}
                    label={`Actions for ${name}`}
                  />
                ) : null}
              </span>
              <DiscordHealth
                checking={checking}
                guild={guild}
                onCheck={onCheck}
                writable={writable}
              />
            </li>
          )
        })}
      </ul>
      {writable ? (
        <div className="admin-actions">
          <InstallLink size="sm" writable={writable}>
            Add a server
          </InstallLink>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Member tracking's card, while the deployment offers it (the catalog's `community.members`): the
 * toggle and Check now save at once, and their answer replaces the card's state in the catalog.
 */
function MemberTracking({ catalog }: { catalog: AdminDiscordConnection }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const members = catalog.community?.members
  const update = (next: AdminMemberTracking) => {
    queryClient.setQueryData<AdminDiscordConnection>(["admin-discord"], (before) =>
      before ? { ...before, community: { ...before.community, members: next } } : before,
    )
    // The session says whether the business tracks (the Customers page's default filter).
    void queryClient.invalidateQueries({ queryKey: ["admin-session"] })
  }
  const failed = (error: Error) =>
    toast({
      message:
        error instanceof AdminApiError
          ? error.message
          : "Member tracking could not reach the server. Check your connection, then try again.",
      tone: "error",
    })
  const toggle = useMutation({ mutationFn: setMemberTracking, onError: failed, onSuccess: update })
  const check = useMutation({ mutationFn: checkMembersNow, onError: failed, onSuccess: update })
  if (!members) return null
  return (
    <MemberTrackingCard
      checking={check.isPending}
      guilds={catalog.guilds}
      onCheckNow={() => check.mutate()}
      onTrackingChange={(tracking) => toggle.mutate(tracking)}
      pending={toggle.isPending}
      tracking={members}
      writable={catalog.writable}
    />
  )
}

function validateWebhooks(draft: AdminUpdateDiscordWebhooksRequest) {
  const parsed = adminUpdateDiscordWebhooksRequestSchema.safeParse(draft)
  const errors: Errors = {}
  for (const issue of parsed.error?.issues ?? []) {
    errors[String(issue.path[0])] = "Enter the webhook's full URL, from Discord."
  }
  return errors
}

/**
 * The bot's profile per server (COMMUNITY_FEATURES bot_profile): the card for the server chosen,
 * which loads its draft and what Discord shows now. Apply saves the draft first.
 */
function BotProfile({ catalog }: { catalog: AdminDiscordConnection }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const offered = catalog.community?.botProfile === true
  const [guildId, setGuildId] = useState(
    () => catalog.connection.serverId || catalog.guilds[0]?.id || "",
  )
  const query = useQuery({
    enabled: offered && guildId !== "",
    queryFn: ({ signal }) => getBotProfile(guildId, { signal }),
    queryKey: ["bot-profile", guildId],
  })
  const nameOf = (id: string) =>
    catalog.guilds.find((guild) => guild.id === id)?.name || "your server"
  const failed = (error: Error) =>
    toast({
      message:
        error instanceof AdminApiError
          ? error.message
          : "The bot's profile could not reach the server. Check your connection, then try again.",
      tone: "error",
    })
  // Each request names its server, so one that answers after the picker moved on refreshes and
  // reports the server it changed.
  const refresh = (id: string) => queryClient.invalidateQueries({ queryKey: ["bot-profile", id] })
  const save = useMutation({
    mutationFn: ({ draft, guildId: id }: { draft: AdminBotProfileRequest; guildId: string }) =>
      saveBotProfile(id, draft),
    onError: failed,
    onSuccess: async (_result, { guildId: id }) => {
      await refresh(id)
      toast({ message: "Bot profile saved.", tone: "success" })
    },
  })
  const send = useMutation({
    mutationFn: async ({
      action,
      draft,
      guildId: id,
    }: {
      action: "apply" | "reset"
      draft?: AdminBotProfileRequest
      guildId: string
    }) => {
      if (draft) await saveBotProfile(id, draft)
      return sendBotProfile(id, action)
    },
    onError: async (error, { guildId: id }) => {
      await refresh(id)
      failed(error)
    },
    onSuccess: async (result, { action, guildId: id }) => {
      await refresh(id)
      const server = nameOf(id)
      toast({
        message:
          action === "reset"
            ? `The bot in ${server} is back to Ternary.`
            : result.skipped.includes("nick")
              ? `Applied in ${server}, except the nickname: the bot can't change it there.`
              : `Applied in ${server}.`,
        tone: "success",
      })
    },
  })
  if (!offered || guildId === "") return null
  if (query.isPending) return <FormLoading fields={3} label="Loading the bot's profile…" />
  if (query.isError) {
    return (
      <LoadError
        detail={errorDetail(query.error)}
        message="We couldn't load the bot's profile"
        onRetry={() => void query.refetch()}
      />
    )
  }
  return (
    <BotProfileCard
      busy={save.isPending ? "save" : send.isPending ? (send.variables?.action ?? "apply") : null}
      guildId={guildId}
      guilds={catalog.guilds}
      // A new server, or a saved or applied profile, starts the form from what the server answered.
      key={`${guildId}:${JSON.stringify(query.data.draft)}:${query.data.applied?.at ?? ""}`}
      onApply={(draft) => send.mutate({ action: "apply", draft, guildId })}
      onGuildChange={setGuildId}
      onReset={() => send.mutate({ action: "reset", guildId })}
      onSave={(draft) => save.mutate({ draft, guildId })}
      profile={query.data}
      // Owners and admins: a member-role account never sees buttons that would answer 403.
      writable={query.data.writable}
    />
  )
}

function WebhooksForm({
  catalog,
  id,
  onSave,
}: {
  catalog: AdminDiscordConnection
  id: string
  onSave(settings: AdminUpdateDiscordWebhooksRequest): void
}) {
  const { serverId } = catalog.connection
  const phone = useMedia(PHONE_QUERY)
  // Folded on a phone once both are set; one still to set shows its field.
  const [open, setOpen] = useState(() => !catalog.webhooks.logs || !catalog.webhooks.rewards)
  const { draft, edit, errors, formRef, submit } = useCheckedForm(
    () => ({ logs: catalog.webhooks.logs, rewards: catalog.webhooks.rewards }),
    (value) => validateWebhooks({ logs: value.logs.trim(), rewards: value.rewards.trim() }),
  )
  const fields = (
    <fieldset className="admin-fieldset admin-columns" disabled={!catalog.writable}>
      <Field
        error={errors.logs}
        hint={errors.logs ? undefined : "Purchases, cancellations and applications."}
        label="Logs webhook"
        required={catalog.writable}
      >
        <Input
          autoComplete="off"
          onChange={(event) => edit({ logs: event.target.value }, "logs")}
          placeholder="https://discord.com/api/webhooks/…"
          spellCheck={false}
          type="url"
          value={draft.logs}
        />
      </Field>
      <Field
        error={errors.rewards}
        hint={errors.rewards ? undefined : "Reward requests and member notifications."}
        label="Rewards webhook"
        required={catalog.writable}
      >
        <Input
          autoComplete="off"
          onChange={(event) => edit({ rewards: event.target.value }, "rewards")}
          placeholder="https://discord.com/api/webhooks/…"
          spellCheck={false}
          type="url"
          value={draft.rewards}
        />
      </Field>
    </fieldset>
  )
  return (
    <form
      aria-label="Webhooks"
      id={id}
      noValidate
      onSubmit={(event) =>
        submit(
          event,
          (value) =>
            // The server comes from the connection: a different one is refused (fix d).
            onSave({
              logs: value.logs.trim(),
              rewards: value.rewards.trim(),
              ...(serverId ? { serverId } : {}),
            }),
          // A blocked Save opens the fold, so the invalid field can take focus.
          () => setOpen(true),
        )
      }
      ref={formRef}
    >
      {phone ? (
        <details
          className="admin-fold"
          onToggle={(event) => setOpen(event.currentTarget.open)}
          open={open}
        >
          <summary className="admin-fold-summary">
            Webhooks
            <span aria-hidden="true" className="tny-menu-chevron" />
          </summary>
          {fields}
        </details>
      ) : (
        fields
      )}
    </form>
  )
}

// The Automations tab: the welcome message and the reaction roles, saved as the bot settings.

type ReactionRow = { emoji: string; id: string; roleId: string }
type BotDraft = Omit<AdminUpdateDiscordBotRequest, "reactions"> & { reactions: ReactionRow[] }

function validateBot(draft: BotDraft, publishAvailable: boolean) {
  const errors: Errors = {}
  if (draft.welcomeEnabled) {
    if (!draft.welcomeChannelId.trim()) errors.welcomeChannelId = "Enter the channel ID."
    if (!draft.welcomeMessage.trim()) errors.welcomeMessage = "Enter a message."
  }
  if (draft.reactionRolesEnabled) {
    if (!publishAvailable) {
      errors.reactionRolesEnabled =
        "Reaction roles can't be published while the bot is unavailable. Turn them off to save."
    } else if (draft.reactions.length === 0) {
      errors.reactionRolesEnabled = "Add at least one role."
    }
    if (!draft.reactionChannelId.trim()) errors.reactionChannelId = "Enter the channel ID."
    if (!draft.reactionMessage.trim()) errors.reactionMessage = "Enter a message."
    const seen = new Set<string>()
    for (const row of draft.reactions) {
      if (seen.has(row.emoji)) errors[`${row.id}.emoji`] = "Each emoji can only grant one role."
      if (!row.roleId) errors[`${row.id}.roleId`] = "Choose a role."
      seen.add(row.emoji)
    }
  }
  return errors
}

function AutomationsForm({
  catalog,
  id,
  onSave,
}: {
  catalog: AdminDiscordConnection
  id: string
  onSave(settings: AdminUpdateDiscordBotRequest): void
}) {
  const { bot, roles, writable } = catalog
  const wide = useWide()
  const rowId = useId()
  const addRef = useRef<HTMLButtonElement>(null)
  const [panel, setPanel] = useState("welcome")
  const { draft, edit, errors, formRef, setDraft, submit } = useCheckedForm<BotDraft>(
    () => ({
      reactionChannelId: bot.reactionChannelId,
      reactionMessage: bot.reactionMessage,
      reactionRolesEnabled: bot.reactionRolesEnabled,
      reactions: bot.reactions.map((reaction) => ({ ...reaction, id: crypto.randomUUID() })),
      welcomeChannelId: bot.welcomeChannelId,
      welcomeEnabled: bot.welcomeEnabled,
      welcomeMessage: bot.welcomeMessage,
    }),
    (value) => validateBot(value, bot.publishAvailable),
  )

  function setRow(index: number, row: ReactionRow, field: string) {
    const reactions = [...draft.reactions]
    reactions[index] = row
    edit({ reactions }, `${row.id}.${field}`)
  }

  function addRow() {
    const used = new Set(draft.reactions.map(({ emoji }) => emoji))
    const row = {
      emoji: emojis.find((emoji) => !used.has(emoji)) ?? emojis[0] ?? "🚀",
      id: crypto.randomUUID(),
      roleId: roles[0]?.id ?? "",
    }
    // Rendered now, so the new row's emoji can take focus.
    flushSync(() => edit({ reactions: [...draft.reactions, row] }, "reactionRolesEnabled"))
    document.getElementById(`${rowId}-${row.id}`)?.focus()
  }

  function removeRow(row: ReactionRow) {
    flushSync(() =>
      setDraft((current) => ({
        ...current,
        reactions: current.reactions.filter((each) => each.id !== row.id),
      })),
    )
    // The removed row's button is gone, so focus goes back to Add a role.
    addRef.current?.focus()
  }

  const welcome = (
    <fieldset className="admin-fieldset admin-stack admin-stack-tight" disabled={!writable}>
      <Checkbox
        checked={draft.welcomeEnabled}
        hint="Post a message in a channel when someone joins."
        label="Welcome new members"
        onChange={(event) => edit({ welcomeEnabled: event.target.checked })}
      />
      {draft.welcomeEnabled ? (
        <div className="admin-pair">
          <Field
            error={errors.welcomeChannelId}
            hint={errors.welcomeChannelId ? undefined : CHANNEL_HINT}
            label="Welcome channel ID"
            required={writable}
          >
            <Input
              autoComplete="off"
              inputMode="numeric"
              maxLength={64}
              onChange={(event) =>
                edit({ welcomeChannelId: event.target.value }, "welcomeChannelId")
              }
              spellCheck={false}
              value={draft.welcomeChannelId}
            />
          </Field>
          <MessageField
            error={errors.welcomeMessage}
            label="Welcome channel post"
            onChange={(welcomeMessage) => edit({ welcomeMessage }, "welcomeMessage")}
            required={writable}
            value={draft.welcomeMessage}
          />
        </div>
      ) : null}
    </fieldset>
  )
  const addRoleButton =
    draft.reactionRolesEnabled && writable ? (
      <Button
        disabled={roles.length === 0 || draft.reactions.length >= MAX_REACTIONS}
        onClick={addRow}
        ref={addRef}
        size="sm"
        variant="secondary"
      >
        Add a role
      </Button>
    ) : null
  const reactionRoles = (
    <fieldset className="admin-fieldset admin-stack admin-stack-tight" disabled={!writable}>
      {/* Below 1024px Add a role heads the rows, as Add link does in Branding, so the tab fits a
          phone. From 1024px it sits under the last role, where the next one goes. */}
      <div className="admin-row admin-row-choice">
        <Checkbox
          checked={draft.reactionRolesEnabled}
          error={errors.reactionRolesEnabled}
          hint="Give a role when a member reacts."
          label="Grant roles by reaction"
          onChange={(event) =>
            edit({ reactionRolesEnabled: event.target.checked }, "reactionRolesEnabled")
          }
        />
        {addRoleButton && !wide ? addRoleButton : null}
      </div>
      {draft.reactionRolesEnabled ? (
        <>
          <div className="admin-pair">
            <Field
              error={errors.reactionChannelId}
              hint={errors.reactionChannelId ? undefined : CHANNEL_HINT}
              label="Reaction channel ID"
              required={writable}
            >
              <Input
                autoComplete="off"
                inputMode="numeric"
                maxLength={64}
                onChange={(event) =>
                  edit({ reactionChannelId: event.target.value }, "reactionChannelId")
                }
                spellCheck={false}
                value={draft.reactionChannelId}
              />
            </Field>
            <MessageField
              error={errors.reactionMessage}
              label="Reaction message"
              onChange={(reactionMessage) => edit({ reactionMessage }, "reactionMessage")}
              required={writable}
              value={draft.reactionMessage}
            />
          </div>
          {draft.reactions.map((row, index) => (
            <div className="admin-reaction-row" key={row.id}>
              <Field error={errors[`${row.id}.emoji`]} label={`Emoji ${index + 1}`}>
                <Select
                  id={`${rowId}-${row.id}`}
                  onChange={(event) =>
                    setRow(index, { ...row, emoji: event.target.value }, "emoji")
                  }
                  value={row.emoji}
                >
                  {emojis.map((emoji) => (
                    <option key={emoji} value={emoji}>
                      {emoji}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field error={errors[`${row.id}.roleId`]} label={`Role ${index + 1}`}>
                <Select
                  onChange={(event) =>
                    setRow(index, { ...row, roleId: event.target.value }, "roleId")
                  }
                  value={row.roleId}
                >
                  <option value="">Choose a role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Select>
              </Field>
              {writable ? (
                <IconButton
                  className="admin-link-remove"
                  icon={<NavIcon name="trash" />}
                  label={`Remove role ${index + 1}`}
                  onClick={() => removeRow(row)}
                />
              ) : null}
            </div>
          ))}
          {addRoleButton && wide ? <div className="admin-actions">{addRoleButton}</div> : null}
        </>
      ) : null}
    </fieldset>
  )

  return (
    <form
      aria-label="Automations"
      id={id}
      noValidate
      onSubmit={(event) =>
        submit(
          event,
          ({ reactions, ...value }) =>
            onSave({
              ...value,
              reactions: reactions.map(({ emoji, roleId }) => ({ emoji, roleId })),
            }),
          (found) =>
            setPanel(
              Object.keys(found).some((key) => key.startsWith("welcome")) ? "welcome" : "reactions",
            ),
        )
      }
      ref={formRef}
    >
      {/* Side by side from 1024px. Below it both do not fit one screen, so each is a tab. */}
      {wide ? (
        <div className="admin-columns">
          {welcome}
          {reactionRoles}
        </div>
      ) : (
        <Tabs
          label="Automations"
          onChange={setPanel}
          tabs={[
            { content: welcome, id: "welcome", label: "Welcome channel post" },
            { content: reactionRoles, id: "reactions", label: "Reaction roles" },
          ]}
          value={panel}
        />
      )}
    </form>
  )
}

// The Member messages tab: the embeds the bot sends members.

const EMBED_ERRORS: Record<keyof AdminUpdateDiscordEmbedsRequest, string> = {
  contactUrl: "Enter a full link, such as https://northwind.example/contact.",
  expiredMessage: "Enter a message.",
  failedMessage: "Enter a message.",
  videoUrl: "Enter a full link, or leave this empty.",
  welcomeMessage: "Enter a message.",
}

function embedsRequest(draft: AdminUpdateDiscordEmbedsRequest) {
  return adminUpdateDiscordEmbedsRequestSchema.safeParse({
    ...draft,
    contactUrl: draft.contactUrl.trim(),
    videoUrl: draft.videoUrl.trim(),
  })
}

function MessagesForm({
  catalog,
  id,
  onSave,
}: {
  catalog: AdminDiscordConnection
  id: string
  onSave(settings: AdminUpdateDiscordEmbedsRequest): void
}) {
  const wide = useWide()
  const [panel, setPanel] = useState("messages")
  const { draft, edit, errors, formRef, submit } = useCheckedForm(
    () => catalog.embeds,
    (value) => {
      const found: Errors = {}
      for (const issue of embedsRequest(value).error?.issues ?? []) {
        const key = issue.path[0] as keyof AdminUpdateDiscordEmbedsRequest
        found[key] = EMBED_ERRORS[key]
      }
      return found
    },
  )
  const text = (key: keyof AdminUpdateDiscordEmbedsRequest) => ({
    onChange: (event: { target: { value: string } }) => edit({ [key]: event.target.value }, key),
    value: draft[key],
  })
  const message = (key: "welcomeMessage" | "failedMessage" | "expiredMessage") => ({
    error: errors[key],
    onChange: (value: string) => edit({ [key]: value }, key),
    required: catalog.writable,
    value: draft[key],
  })
  const messages = (
    <div className="admin-stack admin-stack-tight">
      <MessageField
        {...message("welcomeMessage")}
        hint="Sent when someone joins."
        label="Welcome DM"
      />
      <div className="admin-columns">
        <MessageField
          {...message("failedMessage")}
          hint="Sent when a payment fails."
          label="Failed payment message"
        />
        <MessageField
          {...message("expiredMessage")}
          hint="Sent when a membership expires."
          label="Expired membership message"
        />
      </div>
    </div>
  )
  const links = (
    <div className="admin-columns">
      <Field error={errors.contactUrl} label="Contact link" required={catalog.writable}>
        <Input
          autoComplete="url"
          placeholder="https://northwind.example/contact"
          type="url"
          {...text("contactUrl")}
        />
      </Field>
      <Field error={errors.videoUrl} label="Welcome video">
        <Input
          autoComplete="url"
          placeholder="https://youtube.com/watch?v=…"
          type="url"
          {...text("videoUrl")}
        />
      </Field>
    </div>
  )
  return (
    <form
      aria-label="Member messages"
      id={id}
      noValidate
      onSubmit={(event) =>
        submit(
          event,
          (value) => {
            const parsed = embedsRequest(value)
            if (parsed.success) onSave(parsed.data)
          },
          (found) =>
            setPanel(
              Object.keys(found).some((key) => key.endsWith("Message")) ? "messages" : "links",
            ),
        )
      }
      ref={formRef}
    >
      <fieldset
        className="admin-fieldset admin-stack admin-stack-tight"
        disabled={!catalog.writable}
      >
        {/* All at once from 1024px. Below it the messages, each up to three lines with when it is
            sent, and the links do not fit one screen together, so each is a tab. */}
        {wide ? (
          <>
            {messages}
            {links}
          </>
        ) : (
          <Tabs
            label="Member messages"
            onChange={setPanel}
            tabs={[
              { content: messages, id: "messages", label: "Messages" },
              { content: links, id: "links", label: "Links" },
            ]}
            value={panel}
          />
        )}
      </fieldset>
    </form>
  )
}

const TAB_IDS: TabId[] = ["server", "automations", "messages"]

function DiscordSettings({
  catalog,
  checking,
  message,
  onCheck,
  onRemove,
  onTabChange,
  pending,
  saveBot,
  saveEmbeds,
  saveWebhooks,
}: {
  catalog: AdminDiscordConnection
  checking: boolean
  message: Message | null
  onCheck(): Promise<unknown>
  onRemove(): void
  onTabChange(): void
  pending: Record<TabId, boolean>
  saveBot(settings: AdminUpdateDiscordBotRequest): void
  saveEmbeds(settings: AdminUpdateDiscordEmbedsRequest): void
  saveWebhooks(settings: AdminUpdateDiscordWebhooksRequest): void
}) {
  const formId = useId()
  const [params, setParams] = useSearchParams()
  const requested = params.get("tab") as SettingsTab | null
  const tabIds: SettingsTab[] = [
    ...TAB_IDS,
    ...(catalog.community?.polls ? (["polls"] as const) : []),
    // X3: with any community feature, saved posts too (Coming soon until it is offered).
    ...(catalog.community ? (["saved-posts"] as const) : []),
  ]
  const tab = requested && tabIds.includes(requested) ? requested : "server"
  const ids = {
    automations: `${formId}-automations`,
    messages: `${formId}-messages`,
    server: `${formId}-server`,
  }
  const bar = (id: TabId) => (
    <SaveBar
      form={ids[id]}
      message={message?.tab === id ? message : undefined}
      pending={pending[id]}
      writable={catalog.writable}
    />
  )
  // Each form is keyed by what it saves, so a save or a refetch that changes it resets the draft.
  const panels: { badge?: string; content: ReactNode; id: SettingsTab; label: string }[] = [
    {
      id: "server",
      label: "Server",
      content: (
        <div className="admin-stack">
          <ServerList catalog={catalog} checking={checking} onCheck={onCheck} onRemove={onRemove} />
          <MemberTracking catalog={catalog} />
          <BotProfile catalog={catalog} />
          <WebhooksForm
            catalog={catalog}
            id={ids.server}
            key={JSON.stringify(catalog.webhooks)}
            onSave={saveWebhooks}
          />
          {bar("server")}
        </div>
      ),
    },
    {
      id: "automations",
      label: "Automations",
      content: (
        <div className="admin-stack">
          <AutomationsForm
            catalog={catalog}
            id={ids.automations}
            key={JSON.stringify([catalog.bot, catalog.roles])}
            onSave={saveBot}
          />
          {bar("automations")}
        </div>
      ),
    },
    {
      id: "messages",
      label: "Member messages",
      content: (
        <div className="admin-stack">
          <MessagesForm
            catalog={catalog}
            id={ids.messages}
            key={JSON.stringify(catalog.embeds)}
            onSave={saveEmbeds}
          />
          {bar("messages")}
        </div>
      ),
    },
  ]
  if (catalog.community?.polls) {
    panels.push({ content: <PollsPanel catalog={catalog} />, id: "polls", label: "Polls" })
  }
  if (catalog.community?.savedPosts) {
    panels.push({ content: <SavedPostsPanel />, id: "saved-posts", label: "Saved posts" })
  } else if (catalog.community) {
    panels.push({
      badge: "Coming soon",
      content: <SavedPostsComingSoon />,
      id: "saved-posts",
      label: "Saved posts",
    })
  }
  return (
    <Tabs
      label="Discord settings"
      onChange={(next) => {
        onTabChange()
        setParams(
          (current) => {
            current.set("tab", next)
            return current
          },
          { replace: true },
        )
      }}
      tabs={panels}
      value={tab}
    />
  )
}

export function AdminDiscordPage() {
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const query = useQuery({
    queryFn: loadAdminDiscord,
    queryKey: ["admin-discord"],
    // While a member check runs (it takes a minute or two), read again so the card shows its end
    // without a reload; once every server is checked, stop.
    refetchInterval: (current) =>
      current.state.data?.community?.members?.guilds.some(({ state }) => state === "checking")
        ? 10_000
        : false,
  })
  // Once the wizard shows, it stays until Finish, even after the refetch that connects the bot.
  const [settingUp, setSettingUp] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [message, setMessage] = useState<Message | null>(null)
  // The install's return, ?connected=1 or ?error=<code>, is read once and then taken out of the
  // URL, so a reload never shows it again. A tab change or a save dismisses it.
  const [arrival, setArrival] = useState(() => ({
    code: params.get("error"),
    installed: params.get("connected") === "1",
  }))
  const stripArrival = useEffectEvent(() => {
    // Back from adding the bot: the server is on the dashboard's setup checklist (G7).
    if (params.get("connected") === "1") {
      void queryClient.invalidateQueries({ queryKey: ["admin-setup"] })
    }
    if (!params.has("connected") && !params.has("error")) return
    setParams(
      (current) => {
        current.delete("connected")
        current.delete("error")
        return current
      },
      { replace: true },
    )
  })
  useEffect(() => stripArrival(), [])

  function dismissArrival() {
    setArrival({ code: null, installed: false })
  }

  function useSave<T>(
    tab: TabId,
    mutationFn: (input: T) => Promise<AdminDiscordConnection>,
    saved: string,
  ) {
    return useMutation({
      mutationFn,
      onMutate() {
        setMessage(null)
        dismissArrival()
      },
      onError(error) {
        setMessage({
          error: true,
          tab,
          text: error instanceof Error ? error.message : "Discord settings could not be saved.",
        })
      },
      onSuccess(data) {
        setMessage({ tab, text: saved })
        queryClient.setQueryData(["admin-discord"], data)
        // The server and the contact link are on the dashboard's setup checklist (G7).
        void queryClient.invalidateQueries({ queryKey: ["admin-setup"] })
      },
    })
  }

  const bot = useSave("automations", updateAdminDiscordBot, "Automations saved.")
  const webhooks = useSave("server", updateAdminDiscordWebhooks, "Webhooks saved.")
  const embeds = useSave("messages", updateAdminDiscordEmbeds, "Member messages saved.")
  const disconnect = useSave(
    "server",
    () => setAdminDiscordConnection(false),
    "The bot left the server.",
  )

  if (query.isPending || !query.data) {
    return (
      <section aria-busy={query.isPending || undefined} className="admin-stack admin-page">
        <ConnectionsHeader active="discord" />
        {query.isPending ? (
          <FormLoading fields={3} label="Loading Discord settings…" />
        ) : (
          <LoadError
            detail={errorDetail(query.error)}
            message="We couldn't load your Discord settings"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        )}
      </section>
    )
  }

  const catalog = query.data
  const { code, installed } = arrival
  // Own keys only, so a code such as "constructor" gets the generic message, not a function.
  const installError = code
    ? ((Object.hasOwn(INSTALL_ERRORS, code) ? INSTALL_ERRORS[code] : undefined) ??
      "The Discord install didn't finish. Try again.")
    : null
  // The callback says only that a server was added: with one server, it is the first connect.
  const firstServer = installed && catalog.guilds.length <= 1
  const wizard = settingUp || !catalog.connection.connected || firstServer
  if (wizard && !settingUp) setSettingUp(true)

  async function check() {
    const result = await query.refetch()
    if (result.isError) throw new Error("We couldn't check your server. Try again.")
  }

  function finish() {
    setSettingUp(false)
    dismissArrival()
  }

  return (
    <section className="admin-stack admin-page">
      <ConnectionsHeader active="discord" />
      {catalog.writable ? null : <ViewOnlyNotice />}
      {wizard ? (
        <ConnectWizard
          catalog={catalog}
          checking={query.isFetching}
          installError={installError}
          onCheck={check}
          onFinish={finish}
          stepId={firstServer ? "confirm" : undefined}
        />
      ) : (
        <>
          {installError ? (
            <div className="admin-row">
              <p className="tny-error" role="alert">
                {installError}
              </p>
              <InstallLink size="sm" writable={catalog.writable}>
                Try again
              </InstallLink>
            </div>
          ) : null}
          {/* Shown on load, after the install's return, so it needs no region rendered before. */}
          {installed ? (
            <p className="admin-note" role="status">
              Discord server added.
            </p>
          ) : null}
          <DiscordSettings
            catalog={catalog}
            checking={query.isFetching}
            message={message}
            onCheck={() => query.refetch()}
            onRemove={() => setRemoving(true)}
            onTabChange={dismissArrival}
            pending={{
              automations: bot.isPending,
              messages: embeds.isPending,
              server: webhooks.isPending || disconnect.isPending,
            }}
            saveBot={(settings) => bot.mutate(settings)}
            saveEmbeds={(settings) => embeds.mutate(settings)}
            saveWebhooks={(settings) => webhooks.mutate(settings)}
          />
        </>
      )}
      <ConfirmDialog
        body="The bot leaves the server. Members keep the roles they have, but nobody gets a role, a welcome message or a reaction role until you add the bot again."
        confirmLabel="Remove bot"
        danger
        onCancel={() => setRemoving(false)}
        onConfirm={() => disconnect.mutate(undefined, { onSettled: () => setRemoving(false) })}
        open={removing}
        pending={disconnect.isPending}
        title={`Remove the bot from ${catalog.connection.serverName || "your server"}?`}
      />
    </section>
  )
}
